// Preparing a photographed bill for upload.
//
// Two requirements pull against each other. The photograph is the audit trail:
// somebody may have to read a smudged handwritten slip from it two years from
// now, so it has to stay sharp. But it is taken on a phone, over a phone
// connection, by somebody standing in a shop — so a 5 MB original is a slow
// upload and a large backup for every chai bill.
//
// A long edge of 2000px keeps receipt text comfortably legible while cutting a
// typical 4 MB phone photo to a few hundred KB. Quality steps down only if the
// result is still over the ceiling, so a simple bill ends up smaller than a
// dense one rather than everything being squashed to the same degree.
//
// If anything here fails — an unusual format, a browser without the APIs — the
// original file is used unchanged. A slow upload is far better than a person
// in a shop being told their photo is no good.

export type Prepared = {
  blob: Blob;
  /** Bytes before and after, so the screen can show it actually worked. */
  before: number;
  after: number;
  width: number;
  height: number;
  /** False when compression was skipped and the original is being sent. */
  compressed: boolean;
};

const MAX_EDGE = 2000;      // plenty to read a receipt; far below a phone sensor
const CEILING = 900 * 1024; // what we try to come in under
const QUALITIES = [0.85, 0.75, 0.65, 0.55];

export async function preparePhoto(file: File): Promise<Prepared> {
  const before = file.size;
  const fallback = (): Prepared => ({
    blob: file, before, after: before, width: 0, height: 0, compressed: false,
  });

  // A PDF — the accountant's usual supplier invoice — goes up as it is. There
  // is nothing to downscale, and re-encoding would lose its text layer, which
  // is the part that reads best.
  if (file.type === 'application/pdf') return fallback();

  if (typeof createImageBitmap !== 'function') return fallback();

  let bitmap: ImageBitmap;
  try {
    // from-image applies the EXIF rotation, so a portrait photo is not stored
    // on its side — which matters when somebody is trying to read it later.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return fallback();
  }

  try {
    const { width: w0, height: h0 } = bitmap;
    if (!w0 || !h0) return fallback();

    // Never enlarge: a small photo stays exactly as it is.
    const scale = Math.min(1, MAX_EDGE / Math.max(w0, h0));
    const width = Math.max(1, Math.round(w0 * scale));
    const height = Math.max(1, Math.round(h0 * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return fallback();
    // A white ground, so a transparent PNG does not flatten to black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    let best: Blob | null = null;
    for (const q of QUALITIES) {
      const blob = await toBlob(canvas, q);
      if (!blob) break;
      best = blob;
      if (blob.size <= CEILING) break;
    }
    if (!best) return fallback();

    // If our "compression" made it bigger — possible for an already-small or
    // already-optimised image — keep the original.
    if (best.size >= before) return fallback();

    return { blob: best, before, after: best.size, width, height, compressed: true };
  } finally {
    bitmap.close?.();
  }
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/** "3.9 MB", "412 KB" — for showing the saving to somebody who is not technical. */
export function bytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}
